import * as Sentry from '@sentry/react';
import isLocalhost from './isLocalHost';

/**
 * Determines the environment based on REACT_APP_SERVER_DOMAIN
 * Matches backend pattern: beacon.production, beacon.dev, etc.
 */
function getEnvironment(): string {
    const serverDomain = (process.env.REACT_APP_SERVER_DOMAIN || '').toLowerCase();

    if (serverDomain.includes('frontdoor.mymedgas.com')) return 'production';
    if (serverDomain.includes('mmg-staging-api')) return 'staging';
    if (serverDomain.includes('mmg-test-api')) return 'test';
    if (serverDomain.includes('mmg-api-dev')) return 'dev';
    if (serverDomain.includes('localhost')) return 'dev';

    // Default to dev
    return 'dev';
}

/**
 * Sentry reporting for the FSE app. Deliberately narrow: warnings and errors only.
 * No session replay, no performance tracing, no breadcrumbs, no automatic
 * instrumentation of clicks/fetch/navigation - those flooded the previous setup.
 */
class SentryLogger {
    private initialized = false;
    private currentUser: string | null = null;

    init(): void {
        if (this.initialized) return;

        const dsn = process.env.REACT_APP_SENTRY_DSN;
        // Localhost stays silent unless a developer opts in (REACT_APP_SENTRY_ON_LOCALHOST=true) to see events land
        const allowLocalhost = process.env.REACT_APP_SENTRY_ON_LOCALHOST === 'true';
        if ((isLocalhost && !allowLocalhost) || !dsn) {
            console.info('[Sentry]: Disabled (localhost or missing DSN)');
            return;
        }

        try {
            const environment = getEnvironment();

            Sentry.init({
                dsn,
                environment,
                release: process.env.REACT_APP_VERSION || '0.0.0',
                // defaultIntegrations:false is essential - an integrations array alone is MERGED
                // with the defaults, which include breadcrumbs (clicks/fetch/history) and
                // browserSession, and browserSession posts to Sentry on every navigation.
                defaultIntegrations: false,
                integrations: [
                    Sentry.globalHandlersIntegration(),
                    Sentry.browserApiErrorsIntegration(),
                    Sentry.dedupeIntegration(),
                    Sentry.functionToStringIntegration(),
                    Sentry.linkedErrorsIntegration(),
                    Sentry.httpContextIntegration(),
                    // Required for denyUrls/ignoreErrors below to take effect at all.
                    Sentry.eventFiltersIntegration()
                ],
                // Third-party scripts we don't own - carried over from the previous setup.
                denyUrls: [
                    /mixpanel\.com/i,
                    /google-analytics\.com/i,
                    /googletagmanager\.com/i,
                    /doubleclick\.net/i,
                    /facebook\.net/i
                ],
                tracesSampleRate: 0,
                maxBreadcrumbs: 0,
                // Diagnostics payloads nest 4-5 levels (payload > chunks > chunk > fields); the default depth of 3 would print [Object]
                normalizeDepth: 8,
                sendClientReports: false,
                initialScope: { tags: { subsystem: 'FSE' } }
            });

            this.initialized = true;
            console.info(`[Sentry]: Initialized - Environment: ${environment}, Subsystem: FSE`);
        } catch (error) {
            console.error('[Sentry]: Initialization failed', error);
        }
    }

    setUser(email: string): void {
        if (!this.initialized) return;

        try {
            this.currentUser = email;
            Sentry.setUser({ id: email, email });
        } catch (error) {
            console.error('[Sentry]: Failed to set user', error);
        }
    }

    clearUser(): void {
        this.currentUser = null;
        if (!this.initialized) return;

        try {
            Sentry.setUser(null);
        } catch (error) {
            console.error('[Sentry]: Failed to clear user', error);
        }
    }

    /**
     * Report a warning.
     * @param path Component/method path (e.g., "ReduxMiddleware", "ErrorBoundary")
     * @param message Human-readable message
     * @param payload Optional context data
     * @param user Optional user email (uses current user if not provided)
     */
    logWarning(path: string, message: string, payload?: any, user?: string): void {
        if (!this.initialized) return;

        try {
            Sentry.withScope(scope => {
                this.applyScope(scope, 'warning', path, payload, user);
                Sentry.captureMessage(message, 'warning');
            });
        } catch (error) {
            console.error('[Sentry]: Failed to log warning', error);
        }
    }

    /**
     * Report an error. Real Error objects are captured with their stack so Sentry
     * groups them properly; strings fall back to a message.
     */
    logError(path: string, error: Error | string, payload?: any, user?: string): void {
        if (!this.initialized) return;

        try {
            Sentry.withScope(scope => {
                this.applyScope(scope, 'error', path, payload, user);
                if (error instanceof Error) Sentry.captureException(error);
                else Sentry.captureMessage(error, 'error');
            });
        } catch (err) {
            console.error('[Sentry]: Failed to log error', err);
        }
    }

    isEnabled(): boolean {
        return this.initialized;
    }

    /** Waits (bounded) for queued events to be sent - call before a redirect or reload. */
    async flush(timeoutMs = 2000): Promise<void> {
        if (!this.initialized) return;
        try {
            await Sentry.flush(timeoutMs);
        } catch {
            // best effort only
        }
    }

    private applyScope(
        scope: Sentry.Scope,
        level: 'warning' | 'error',
        path: string,
        payload?: any,
        user?: string
    ): void {
        scope.setLevel(level);
        scope.setTag('path', path);
        scope.setTransactionName(path);

        const email = user || this.currentUser;
        if (email) scope.setUser({ id: email, email });
        if (payload) scope.setExtra('Payload', payload);
    }
}

// Export singleton instance
export default new SentryLogger();
